import { isValid, parse } from 'date-fns';
import { formatWithTimeZone, getDateTimeFormat, TimeFormat } from './formatters';
import { isDurationString } from './time-range';

/**
 * A flexible time value that can be either a Date or a relative time expression like "now" or "now-5m"
 */
export type FlexibleTimeValue = Date | string | null;

// TODO: Use TimeRangeInputValue instead unless we really need `null` for some reason?
export interface FlexibleTimeRange {
  end: FlexibleTimeValue;
  start: FlexibleTimeValue;
}

/**
 * Regular expression to extract duration from "now-x" format.
 * Captures the duration part after "now-"
 */
const NOW_MINUS_REGEX = /^now-(\S+)$/i;

/**
 * Checks if a value is a relative time expression (e.g., "now", "now-5m", "now-1h")
 * For "now-x" format, validates that x is a valid duration with units.
 * "now-3" is invalid (no unit), "now-3m" is valid.
 *
 * For bare durations like "5m", "1h", "1h30m", etc., use isDurationString to check if it is a valid duration.
 */
export function isRelativeTimeValue(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.toLowerCase() === 'now') return true;

  // Check for "now-x" format and validate the duration part
  const match = trimmed.match(NOW_MINUS_REGEX);
  if (match && match[1]) {
    return isDurationString(match[1]);
  }

  return false;
}

/**
 * Normalizes a time input value:
 * - Bare durations like "5m" become "now-5m"
 * - Other values are returned as-is
 */
export function normalizeTimeInput(value: string): string {
  const trimmed = value.trim();
  if (isDurationString(trimmed)) {
    return `now-${trimmed}`;
  }
  return trimmed;
}

/**
 * Extracts the duration string from a "now-x" expression.
 * Returns the duration (e.g., "5m" from "now-5m") or null if not a "now-x" format.
 * Returns null for plain "now" (use isNowValue to check for that).
 */
export function extractDurationFromNowMinus(value: string): string | null {
  const match = value.trim().match(NOW_MINUS_REGEX);
  return match ? (match[1] ?? null) : null;
}

/**
 * Checks if a value is exactly "now" (not "now-x")
 */
export function isNowValue(value: string): boolean {
  return value.trim().toLowerCase() === 'now';
}

/**
 * Formats a Date to a string using the specified time format and optional timezone.
 */
export function formatDateToString(date: Date | null, timeFormat: TimeFormat, timeZone?: string): string {
  if (!date || !isValid(date)) return '';
  const format = getDateTimeFormat({ timeFormat });
  return formatWithTimeZone(date, format, timeZone);
}

/**
 * Parses a date string to a Date object.
 * Returns null if the string cannot be parsed.
 * Only accepts dates in the expected format - does NOT use native Date parsing fallback
 * to avoid unexpected interpretations of invalid inputs.
 */
export function parseDateString(value: string, timeFormat: TimeFormat): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Reject strings that look like relative time expressions (even invalid ones)
  // to prevent them from being parsed as dates
  if (trimmed.toLowerCase().startsWith('now')) return null;

  // Reject bare numbers or number-like strings that could be misinterpreted
  if (/^\d+$/.test(trimmed)) return null;

  const format = getDateTimeFormat({ timeFormat });

  // Try parsing with the expected format only
  const parsed = parse(trimmed, format, new Date());
  if (isValid(parsed)) {
    // Additional sanity check: parsed date should be within a reasonable range
    // (not year 1 AD or year 9999)
    const year = parsed.getFullYear();
    if (year >= 1970 && year <= 2100) {
      return parsed;
    }
  }

  return null;
}

/**
 * Validates a flexible time value for the "start" field.
 * Valid if: it's a valid Date, or it's "now-x" (NOT plain "now"),
 * or it's a bare duration ("5m"), or it's empty/null
 */
export function isValidStartTimeValue(value: FlexibleTimeValue, timeFormat: TimeFormat): boolean {
  if (value === null || value === undefined) return true;

  if (value instanceof Date) {
    return isValid(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    const match = trimmed.match(NOW_MINUS_REGEX);
    if (match && match[1]) {
      return isDurationString(match[1]);
    }
    if (isDurationString(trimmed)) return true;

    // Try to parse as a date
    const parsed = parseDateString(trimmed, timeFormat);
    return parsed !== null;
  }

  return false;
}

/**
 * Validates an end time value.
 * Valid if: it's a valid Date, or it's exactly "now", or it's empty/null.
 * Does NOT accept "now-x" or bare durations
 */
export function isValidEndTimeValue(value: FlexibleTimeValue, timeFormat: TimeFormat): boolean {
  if (value === null || value === undefined) return true;

  if (value instanceof Date) {
    return isValid(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    if (isNowValue(trimmed)) return true;

    // Try to parse as a date
    const parsed = parseDateString(trimmed, timeFormat);
    return parsed !== null;
  }

  return false;
}

/**
 * Converts a FlexibleTimeValue to a Date if possible, otherwise returns null.
 * Returns null for relative time expressions like "now" or "now-5m".
 */
export function toDateOrNull(value: FlexibleTimeValue, timeFormat: TimeFormat): Date | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || isRelativeTimeValue(trimmed)) return null;
    return parseDateString(trimmed, timeFormat);
  }

  return null;
}
